import type { ResultOf } from '@horof/shared';
import { graphql } from './gql';

/** Transfers, loans and in-kind contributions (arqam doc 51). */

export const TransfersDocument = graphql(`
  query DeskTransfers {
    accountTransfers(orderBy: [DATE_DESC]) {
      nodes {
        id
        date
        amount
        description
        needsReview
        reviewNote
        fromAccount {
          id
          name
        }
        toAccount {
          id
          name
        }
        receiptPhoto {
          id
          url
          contentType
        }
      }
    }
  }
`);
export type TransferRow = NonNullable<
  NonNullable<
    ResultOf<typeof TransfersDocument>['accountTransfers']
  >['nodes'][number]
>;

export const TransferDocument = graphql(`
  query DeskTransfer($id: UUID!) {
    accountTransfer(id: $id) {
      id
      date
      amount
      description
      fromAccountId
      toAccountId
      needsReview
      reviewNote
      receiptPhotoId
      receiptPhoto {
        id
        url
        contentType
        uploadedBy
      }
    }
  }
`);

export const CreateTransferDocument = graphql(`
  mutation DeskCreateTransfer($transfer: AccountTransferInput!) {
    createAccountTransfer(input: { accountTransfer: $transfer }) {
      accountTransfer {
        id
      }
    }
  }
`);

export const UpdateTransferDocument = graphql(`
  mutation DeskUpdateTransfer($id: UUID!, $patch: AccountTransferPatch!) {
    updateAccountTransfer(input: { id: $id, patch: $patch }) {
      accountTransfer {
        id
        date
        amount
        description
        fromAccountId
        toAccountId
        needsReview
        reviewNote
        receiptPhotoId
      }
    }
  }
`);

export const DeleteTransferDocument = graphql(`
  mutation DeskDeleteTransfer($id: UUID!) {
    deleteAccountTransfer(input: { id: $id }) {
      accountTransfer {
        id
      }
    }
  }
`);

export const DebtsDocument = graphql(`
  query DeskDebts {
    debts(orderBy: [DATE_DESC]) {
      nodes {
        id
        direction
        amount
        outstanding
        date
        purpose
        counterparty {
          id
          name
        }
        project {
          id
          name
        }
      }
    }
  }
`);
export type DebtRow = NonNullable<
  NonNullable<ResultOf<typeof DebtsDocument>['debts']>['nodes'][number]
>;

export const DebtDocument = graphql(`
  query DeskDebt($id: UUID!) {
    debt(id: $id) {
      id
      direction
      amount
      outstanding
      date
      purpose
      counterpartyId
      projectId
      counterparty {
        id
        name
      }
      financialOperations(orderBy: [DATE_ASC, CREATED_AT_ASC]) {
        totalCount
        nodes {
          id
          date
          type
          amount
          description
          account {
            name
          }
        }
      }
    }
  }
`);

export const CreateDebtDocument = graphql(`
  mutation DeskCreateDebt($debt: DebtInput!) {
    createDebt(input: { debt: $debt }) {
      debt {
        id
      }
    }
  }
`);

export const UpdateDebtDocument = graphql(`
  mutation DeskUpdateDebt($id: UUID!, $patch: DebtPatch!) {
    updateDebt(input: { id: $id, patch: $patch }) {
      debt {
        id
        direction
        amount
        outstanding
        date
        purpose
        counterpartyId
        projectId
      }
    }
  }
`);

export const DeleteDebtDocument = graphql(`
  mutation DeskDeleteDebt($id: UUID!) {
    deleteDebt(input: { id: $id }) {
      debt {
        id
      }
    }
  }
`);

/** A new operation from a loan's page: what it takes from the loan. */
export const DebtForOperationDocument = graphql(`
  query DeskDebtForOperation($id: UUID!) {
    debt(id: $id) {
      id
      direction
      purpose
      projectId
      counterpartyId
      counterparty {
        name
      }
    }
  }
`);

export const InKindListDocument = graphql(`
  query DeskInKindList {
    inKindContributions(orderBy: [DATE_DESC]) {
      nodes {
        id
        date
        description
        estimatedValue
        valuationBasis
        needsReview
        counterparty {
          id
          name
        }
        project {
          id
          name
        }
        documentPhoto {
          id
          url
          contentType
        }
      }
    }
  }
`);
export type InKindRow = NonNullable<
  NonNullable<
    ResultOf<typeof InKindListDocument>['inKindContributions']
  >['nodes'][number]
>;

export const InKindDocument = graphql(`
  query DeskInKind($id: UUID!) {
    inKindContribution(id: $id) {
      id
      date
      description
      estimatedValue
      valuationBasis
      counterpartyId
      projectId
      needsReview
      reviewNote
      documentPhotoId
      documentPhoto {
        id
        url
        contentType
        uploadedBy
      }
    }
  }
`);

export const CreateInKindDocument = graphql(`
  mutation DeskCreateInKind($contribution: InKindContributionInput!) {
    createInKindContribution(input: { inKindContribution: $contribution }) {
      inKindContribution {
        id
      }
    }
  }
`);

export const UpdateInKindDocument = graphql(`
  mutation DeskUpdateInKind($id: UUID!, $patch: InKindContributionPatch!) {
    updateInKindContribution(input: { id: $id, patch: $patch }) {
      inKindContribution {
        id
        date
        description
        estimatedValue
        valuationBasis
        counterpartyId
        projectId
        needsReview
        reviewNote
        documentPhotoId
      }
    }
  }
`);

export const DeleteInKindDocument = graphql(`
  mutation DeskDeleteInKind($id: UUID!) {
    deleteInKindContribution(input: { id: $id }) {
      inKindContribution {
        id
      }
    }
  }
`);
